import { X } from "lucide-react";
import { useState } from "react";

export default function GroupSelectorModal({
  open,
  onClose,
  groups = [],
  selectedGroupIds = [],
  setSelectedGroupIds = () => {},
}) {
  if (!open) return null;

  const [searchQuery, setSearchQuery] = useState("");

  const filteredGroups = groups.filter((group) =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleGroupSelect = (groupId) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  const selectAll = () => {
    setSelectedGroupIds(filteredGroups.map((g) => g.id));
  };

  const deselectAll = () => {
    setSelectedGroupIds([]);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex justify-center items-center">
      <div className="bg-white dark:bg-[#1f1f1f] p-6 rounded-2xl shadow-2xl w-full max-w-xl relative max-h-[90vh] overflow-y-auto border dark:border-gray-700">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>

        <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-6">
          Select Groups
        </h3>

        {/* Search */}
        <input
          type="text"
          placeholder="Search groups..."
          className="w-full mb-4 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm text-gray-700 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        {/* Select All */}
        <div className="flex items-center justify-between mb-3">
          <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={
                selectedGroupIds.length === filteredGroups.length &&
                filteredGroups.length > 0
              }
              onChange={() =>
                selectedGroupIds.length === filteredGroups.length
                  ? deselectAll()
                  : selectAll()
              }
              className="accent-blue-600 w-4 h-4"
            />
            <span>Select All</span>
          </label>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {selectedGroupIds.length} selected
          </span>
        </div>

        {/* Group List */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 max-h-[300px] overflow-y-auto px-2 py-2">
          {filteredGroups.length === 0 ? (
            <p className="text-sm text-gray-500 px-2">No matching groups found.</p>
          ) : (
            <ul className="space-y-1">
              {filteredGroups.map((group) => {
                const contactCount = group.contacts?.length || 0;
                const isChecked = selectedGroupIds.includes(group.id);

                return (
                  <li
                    key={group.id}
                    className="flex items-center justify-between text-sm bg-white dark:bg-gray-900 px-3 py-2 rounded-md hover:shadow-sm border border-transparent hover:border-gray-300 dark:hover:border-gray-600 transition"
                  >
                    <label className="flex items-center space-x-2 w-full cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleGroupSelect(group.id)}
                        className="accent-blue-600 w-4 h-4"
                      />
                      <span className="truncate text-gray-800 dark:text-gray-100">
                        {group.name} ({contactCount} {contactCount === 1 ? "contact" : "contacts"})
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Modal Footer */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-md text-gray-700 bg-gray-200 hover:bg-gray-300 dark:text-white dark:bg-gray-700 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
